import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { track, trackPageView } from "../lib/track";
import { initGads } from "../lib/gads";
import LeadModal from "../components/LeadModal";
import NotifyModal from "../components/NotifyModal";
import WhatsAppFab from "../components/WhatsAppFab";
import PaymentSoonModal from "../components/PaymentSoonModal";

const SiteContext = createContext(null);

// Module-level guards so each library injects EXACTLY once, even when React 18
// StrictMode double-invokes the effect (double-injecting fbevents.js corrupts
// fbq and silently blocks all /tr beacons — PageView/Lead never reach Meta).
const _injected = { ga4: false, gtm: false, meta: false };
function injectTracking(s) {
    if (!s) return;
    initGads();
    // GA4
    if (s.ga4_id && !_injected.ga4 && !window.gtag) {
        _injected.ga4 = true;
        const el = document.createElement("script");
        el.id = "ga4-lib"; el.async = true;
        el.src = `https://www.googletagmanager.com/gtag/js?id=${s.ga4_id}`;
        document.head.appendChild(el);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag("js", new Date());
        window.gtag("config", s.ga4_id);
    }
    // GTM
    if (s.gtm_id && !_injected.gtm && !document.getElementById("gtm-lib")) {
        _injected.gtm = true;
        const el = document.createElement("script");
        el.id = "gtm-lib";
        el.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${s.gtm_id}');`;
        document.head.appendChild(el);
    }
    // Meta Pixel — guard on window.fbq (set synchronously by the snippet) so a
    // StrictMode re-run can never load fbevents.js a second time. The initial
    // PageView is fired by the route-change effect once fbq is ready.
    if (s.meta_pixel_id && !_injected.meta && !window.fbq) {
        _injected.meta = true;
        const el = document.createElement("script");
        el.id = "meta-pixel";
        el.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${s.meta_pixel_id}');`;
        document.head.appendChild(el);
    }
}

export function SiteProvider({ children }) {
    const [settings, setSettings] = useState(null);
    const [lead, setLead] = useState({ open: false, service: "General" });
    const [notify, setNotify] = useState({ open: false, item: null });
    const [paymentSoon, setPaymentSoon] = useState({ open: false, item: null });
    const location = useLocation();
    const lastPv = React.useRef("");

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    }, []);
    useEffect(() => { if (settings) injectTracking(settings); }, [settings]);
    // Fire a PageView on every SPA route change (deduped so StrictMode/re-renders
    // don't send the same path twice).
    useEffect(() => {
        if (!settings || !(settings.meta_pixel_id || settings.ga4_id || settings.gtm_id)) return;
        const path = location.pathname + location.search;
        if (lastPv.current === path) return;
        lastPv.current = path;
        trackPageView(path);
    }, [location.pathname, location.search, settings]);

    const trackEvent = useCallback((event, params = {}, opts = {}) => { track(event, params, opts); }, []);

    const openLead = useCallback((service = "General") => {
        setLead({ open: true, service });
        trackEvent("Contact", { service });
    }, [trackEvent]);

    const openNotify = useCallback((item) => {
        setNotify({ open: true, item });
        trackEvent("CourseView", { slug: item?.slug });
    }, [trackEvent]);

    const openPaymentSoon = useCallback((item = null) => {
        setPaymentSoon({ open: true, item });
        trackEvent("PaymentSoon", { slug: item?.slug });
    }, [trackEvent]);

    const waLink = useCallback((key) => {
        const num = (settings?.whatsapp_number || "").replace(/\D/g, "");
        const msg = (settings?.whatsapp_service_msgs && settings.whatsapp_service_msgs[key]) || settings?.whatsapp_default_msg || "Hi MUVIARTS";
        return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
    }, [settings]);

    const openWhatsApp = useCallback((key) => {
        api.post("/track/whatsapp").catch(() => {});
        trackEvent("WhatsAppClick", { key });
        window.open(waLink(key), "_blank");
    }, [waLink, trackEvent]);

    return (
        <SiteContext.Provider value={{ settings, trackEvent, openLead, openNotify, openPaymentSoon, waLink, openWhatsApp }}>
            {children}
            <LeadModal open={lead.open} service={lead.service} onClose={() => setLead({ ...lead, open: false })} trackEvent={trackEvent} openWhatsApp={openWhatsApp} />
            <NotifyModal open={notify.open} item={notify.item} onClose={() => setNotify({ ...notify, open: false })} trackEvent={trackEvent} />
            <PaymentSoonModal open={paymentSoon.open} item={paymentSoon.item} onClose={() => setPaymentSoon({ open: false, item: null })} openWhatsApp={openWhatsApp} />
            <WhatsAppFab openWhatsApp={openWhatsApp} />
        </SiteContext.Provider>
    );
}

export const useSite = () => useContext(SiteContext) || {};
